<script setup lang="ts">
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import FilterChip from './Filters/FilterChip.vue';
import useLocale from '../../composables/useLocale';
import { FilterOption } from '../../search-panel-types';
import { useDisplay } from 'vuetify';
import type { SortOption } from '../use-sort-options';
import { useSearchFilterStore } from '../../use-search-filter';

type Emits = {
  (e: 'change-sort', sortOption: SortOption): void;
};
const emit = defineEmits<Emits>();

type Props = {
  sortOptions: SortOption[];
  totalHits: number;
  isLoading: boolean;
  selectedSortOption?: string;
};
const props = withDefaults(defineProps<Props>(), {
  totalHits: 0,
  isLoading: false,
  selectedSortOption: undefined,
});

const { smAndUp } = useDisplay();
const searchFilterStore = useSearchFilterStore();
const { selectedFilters } = storeToRefs(searchFilterStore);
const { removeSelectedFilterByValue, removeAllFilters } = searchFilterStore;
const { t } = useLocale();

const selectedSortOption = ref<SortOption>(
  props.sortOptions.find((s) => s.key === props.selectedSortOption) ?? props.sortOptions[0],
);

const handleRemoveFilter = (filterKey: string, value: FilterOption): void => {
  removeSelectedFilterByValue(filterKey, value);
};

const handleRemoveAllFilters = () => {
  removeAllFilters();
};

watch([selectedSortOption], () => emit('change-sort', selectedSortOption.value));
</script>

<template>
  <div class="d-flex flex-wrap">
    <div>
      <v-btn
        v-if="sortOptions.length > 0"
        class="sort-btn rounded-lg text-none my-auto"
        color="inverse-primary"
        variant="flat"
        :prepend-icon="selectedSortOption.icon"
      >
        {{ selectedSortOption.label }}
        <v-icon size="x-large" icon="mdi-menu-down"></v-icon>
        <v-menu activator="parent">
          <v-list class="pa-0" :aria-label="t('content_search.search.sort.none')">
            <v-item-group v-model="selectedSortOption">
              <v-item v-for="option in sortOptions" :key="option.label" v-slot="{ isSelected, toggle }" :value="option">
                <v-list-item v-if="!isSelected" :active="isSelected" color="primary" @click="toggle">
                  <template #prepend>
                    <v-icon class="opacity-100" :icon="option.icon"></v-icon>
                  </template>
                  <template #title>
                    {{ option.label }}
                  </template>
                </v-list-item>
              </v-item>
            </v-item-group>
          </v-list>
        </v-menu>
      </v-btn>
      <v-btn
        class="cursor-default rounded-lg text-none ml-4 mr-2 disabled-btn"
        text-color="black"
        variant="flat"
        prepend-icon="mdi-magnify"
        :text="smAndUp ? `${t('content_search.label_search_results')} (${totalHits})` : totalHits.toString()"
        :count="smAndUp ? totalHits : undefined"
        :is-loading="isLoading"
        disabled
      />
      <template v-for="(i, key) in selectedFilters.value" :key="key">
        <template v-for="item in selectedFilters.value[key]" :key="item.label">
          <FilterChip
            v-if="item.label"
            :is-selected="true"
            :label="item.label"
            :aria-label="`${t('button_remove')} ${item.label}`"
            @click:close="handleRemoveFilter(key as string, item)"
          />
        </template>
      </template>
      <FilterChip
        v-if="Object.keys(selectedFilters.value).length > 0"
        :is-selected="true"
        color="bg-error-container"
        text-color="on-error-container"
        :label="t('content_search.label_remove_all_filters')"
        @click:close="handleRemoveAllFilters"
        @click="handleRemoveAllFilters"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sort-btn {
  font-size: 0.875rem;
  padding: 0 11px;
}
.v-btn {
  height: 36px !important;
}
.v-chip {
  height: 36px !important;
  border-radius: 8px;
}
:deep(.disabled-btn.v-btn--disabled) {
  opacity: 1;
  background-color: rgb(var(--v-theme-secondary-container));
  .v-btn__content {
    color: black;
  }
  .v-btn__prepend {
    .v-icon {
      color: black;
    }
  }
}
</style>
